import React, { useState, useCallback } from 'react'
import {
    Box,
    CircularProgress,
    makeStyles,
    Tab,
    Tabs,
    Theme,
    Typography,
} from '@material-ui/core'
import { Company } from '../../../../models'
import { OrganizationSampleW9s } from './OrganizationSampleW9s'
import { OrganizationVendorW9s } from './OrganizationVendorW9s'

type W9TabType = 'sample' | 'vendor'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2),
    },
    tab: {
        textTransform: 'none',
        minWidth: 120,
        fontWeight: 500,
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
    },
}))

interface OrganizationW9ManagerProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
    propertyList: Company[]
}

// Loading fallback component
const TabLoading: React.FC = () => {
    const classes = useStyles()
    return (
        <Box className={classes.loadingContainer}>
            <CircularProgress size={32} />
        </Box>
    )
}

export const OrganizationW9Manager: React.FC<OrganizationW9ManagerProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
    propertyList,
}) => {
    const classes = useStyles()
    const [activeTab, setActiveTab] = useState<W9TabType>('sample')

    const handleTabChange = useCallback(
        (_event: React.ChangeEvent<{}>, newValue: W9TabType) => {
            setActiveTab(newValue)
        },
        [],
    )

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography
                    variant="h5"
                    component="h2"
                    className={classes.title}
                >
                    W-9 Manager
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Manage sample and vendor W-9 forms across all properties.
                </Typography>
            </Box>

            <Box className={classes.tabsContainer}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab
                        label="Sample W-9s"
                        value="sample"
                        className={classes.tab}
                    />
                    <Tab
                        label="Vendor W-9s"
                        value="vendor"
                        className={classes.tab}
                    />
                </Tabs>
            </Box>

            {activeTab === 'sample' && (
                <OrganizationSampleW9s
                    organization={organization}
                    selectedTagId={selectedTagId}
                    onSelectedTagIdChange={onSelectedTagIdChange}
                    propertyList={propertyList}
                />
            )}
            {activeTab === 'vendor' && (
                <OrganizationVendorW9s
                    organization={organization}
                    selectedTagId={selectedTagId}
                    onSelectedTagIdChange={onSelectedTagIdChange}
                />
            )}
        </Box>
    )
}
